import { AuthEndpoint } from '@/api/endpoints/AuthEndpoint';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import { AlertColor, dispatchAlert } from '@/utils/alerts';
import createApp, { AppConfigV2 } from '@shopify/app-bridge';
import { getSessionToken } from '@shopify/app-bridge/utilities';

interface IUserInfo {
  email: string;
  token: string | null;
  lastLogin: number | null;
  lastUse: number | null;
}

export interface IUserState {
  userInfo: IUserInfo;
  loading: boolean;
  shopifyInfo: IShopifyInfo;
}
export interface IShopifyInfo {
  token: string | null;
  expirationTime: number;
}

class UserModule implements Module<IUserState, any> {
  namespaced: boolean = true;

  state: IUserState = {
    userInfo: {
      email: '',
      token: null,
      lastLogin: null,
      lastUse: null,
    },
    loading: false,
    shopifyInfo: {
      token: null,
      expirationTime: 0,
    },
  };

  getters: GetterTree<IUserState, any> = {
    email(state): string {
      return state.userInfo.email;
    },

    token(state): string | null {
      return state.userInfo.token;
    },

    loading(state): boolean {
      return state.loading;
    },
  };

  actions: ActionTree<IUserState, any> = {
    async login(
      { state, rootState, commit, dispatch },
      userInfo: { email: string; password: string },
    ) {
      commit('setLoading', true);

      try {
        const authClient = new AuthEndpoint();
        const loginRes = await authClient.login(userInfo.email, userInfo.password);

        if (loginRes) {
          commit('setToken', loginRes.token);
          commit('setEmail', userInfo.email);
          commit('setLastLogin', Date.now());
        }
      } catch (err) {
        console.error(err);
        const errorMessage = (err as any).title || `An error occurred during login`;
        dispatchAlert(AlertColor.ERROR, errorMessage, dispatch);
        throw err; // throw error to let login page know that login failed
      } finally {
        commit('setLoading', false);
        dispatch('updateLastUse');
      }
    },

    async shopifyLogin({ state, commit, dispatch }): Promise<string> {
      commit('setLoading', true);

      if (
        state.shopifyInfo.token &&
        state.shopifyInfo.expirationTime > new Date().valueOf()
      ) {
        return state.shopifyInfo.token;
      } else {
        try {
          const authClient = new AuthEndpoint();
          const appCreds: AppConfigV2 = {
            ...(await authClient.getShopifyCreds()),
            // The host of the specific shop that's embedding app.
            // This value is provided by Shopify as a URL query parameter
            // that's appended to application URL when app is loaded inside the Shopify admin.
            // https://shopify.dev/docs/apps/tools/app-bridge/getting-started/app-setup#initialize-shopify-app-bridge-in-your-app
            host: new URLSearchParams(location.search).get('host') ?? '',
          };

          const app = createApp(appCreds);

          const sessionToken = await getSessionToken(app);
          commit('setShopifyToken', sessionToken);

          return sessionToken;
        } catch (err) {
          console.error('Shopify authorization error', err);
          return '';
        } finally {
          commit('setLoading', false);
        }
      }
    },

    async logout({ commit, dispatch }) {
      try {
        const authClient = new AuthEndpoint();
        await authClient.logout();

        commit('setToken', null);
        commit('setEmail', '');
        commit('setLastLogin', null);
        commit('setLastUse', null);
      } catch (err) {
        console.error(err);
        const errorMessage = (err as any).title || `An error occurred during logout`;
        dispatchAlert(AlertColor.ERROR, errorMessage, dispatch);
        throw err; // throw error to let login page know that login failed
      } finally {
        commit('setLoading', false);
        dispatch('updateLastUse');
      }
    },

    /**
     * Check is the user is authorized and return a token. \
     * If app is in the iframe return Shopify token. \
     * Otherwise, return stored token.
     * @param param0
     */
    async checkAuth({ state, dispatch, getters }): Promise<boolean> {
      // Use Shopify Login instead of credentials
      if (window.self !== window.top) return this.dispatch('userModule/shopifyLogin');

      if (!state.userInfo.token) return false;

      const daysPassedFromDate = (date: number) => {
        const oneDay = 24 * 60 * 60 * 1000;
        return Math.abs((Date.now() - date) / oneDay);
      };

      // login required every 30 days
      if (state.userInfo.lastLogin) {
        if (daysPassedFromDate(state.userInfo.lastLogin) > 30) {
          dispatch('logout');
          return false;
        }
      }

      // login required after 3 days of inactivity
      if (state.userInfo.lastUse) {
        if (daysPassedFromDate(state.userInfo.lastUse) > 3) {
          dispatch('logout');
          return false;
        }
      }

      return getters['token'];
    },

    updateLastUse({ commit }) {
      commit('setLastUse', Date.now());
    },
  };

  mutations: MutationTree<IUserState> = {
    setToken(state, token: string) {
      state.userInfo.token = token;
    },
    setEmail(state, email: string) {
      state.userInfo.email = email;
    },
    setLastLogin(state, date: number) {
      state.userInfo.lastLogin = date;
    },
    setLastUse(state, date: number) {
      state.userInfo.lastUse = date;
    },
    setLoading(state, value: boolean) {
      state.loading = value;
    },
    setShopifyToken(state, token: string | undefined) {
      if (token) {
        state.shopifyInfo.token = token;
        const currentTime = new Date();
        //Shopify token could live only 1 minute
        state.shopifyInfo.expirationTime = new Date()
          .setSeconds(currentTime.getSeconds() + 55)
          .valueOf();
      } else {
        state.shopifyInfo.token = null;
        state.shopifyInfo.expirationTime = 0;
      }
    },
  };
}

const userModule = new UserModule();
export default userModule;
